import type { Database } from "@revelate/types";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import type {
  PipelineRiskL2Insights,
  PipelineRiskManagementAction,
} from "./pipelineRiskInsights";

type Currency = Database["public"]["Tables"]["currencies"]["Row"] | null;

export type PipelineRiskSectionCopy = {
  heading: string;
  label: string;
  supporting?: string;
};

function formatShare(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en", {
    maximumFractionDigits: 1,
    style: "percent",
  }).format(value);
}

function pluralise(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

export function getPipelineRiskExecutiveCopy(
  insights: PipelineRiskL2Insights,
  companyCurrency: Currency,
) {
  const position = insights.currentPosition;
  if (position.state === "no_high_risk_exposure") {
    if (position.exposedDealCount === 0) {
      return {
        conclusion: "No Pipeline Risk exposure in the selected period.",
        explanation: "No open deal has a Pipeline Risk indicator.",
      };
    }
    return {
      conclusion: "No Forecast is exposed to High Pipeline Risk.",
      explanation: "Medium and Low risks remain visible in the severity analysis.",
    };
  }
  const dominantTheme = insights.drivers.dominantTheme;
  const themeEvidence = dominantTheme
    ? insights.drivers.state === "several_equal_themes"
      ? "Several risk themes have equal Deal value."
      : `${dominantTheme.label} is the largest risk theme by Deal value.`
    : undefined;
  return {
    conclusion: `${formatCompactIsoCurrency(position.highRiskForecastValue, companyCurrency)} of Forecast is exposed to High risk.`,
    explanation: `${formatCompactIsoCurrency(position.highRiskOpenDealValue, companyCurrency)} sits in ${position.highRiskDealCount} High-risk ${pluralise(position.highRiskDealCount, "deal")}.`,
    evidence: themeEvidence,
  };
}

export function getPipelineRiskPositionCopy(
  insights: PipelineRiskL2Insights,
  companyCurrency: Currency,
): PipelineRiskSectionCopy {
  const position = insights.currentPosition;
  if (position.state === "no_high_risk_exposure") {
    if (position.exposedDealCount === 0) {
      return {
        heading: "No open deals show Pipeline Risk exposure",
        label: "Current exposure",
      };
    }
    return {
      heading: "No Forecast is exposed to High Pipeline Risk",
      label: "Current exposure",
      supporting: "Medium and Low risks remain visible below.",
    };
  }
  return {
    heading: `${formatShare(position.highRiskForecastShare)} of Forecast depends on High-risk deals`,
    label: "Current exposure",
    supporting: `${formatCompactIsoCurrency(position.highRiskForecastValue, companyCurrency)} of Forecast across ${position.highRiskDealCount} ${pluralise(position.highRiskDealCount, "deal")}.`,
  };
}

export function getPipelineRiskMovementCopy(
  insights: PipelineRiskL2Insights,
  companyCurrency: Currency,
): PipelineRiskSectionCopy {
  const movement = insights.movement;
  if (!movement?.available) {
    return {
      heading: "30-day Pipeline Risk movement is unavailable",
      label: "What changed",
      supporting: "No like-for-like cohort has enough history for this selection.",
    };
  }

  const change = movement.netHighRiskDealValueChange;
  const heading = change > 0
    ? `High-risk deal value increased by ${formatCompactIsoCurrency(change, companyCurrency)}`
    : change < 0
      ? `High-risk deal value decreased by ${formatCompactIsoCurrency(Math.abs(change), companyCurrency)}`
      : "High-risk deal value remained stable";
  const exposureMovement = [
    movement.enteredHighRisk.dealValue > 0
      ? `${formatCompactIsoCurrency(movement.enteredHighRisk.dealValue, companyCurrency)} entered High risk`
      : null,
    movement.leftHighRisk.dealValue > 0
      ? `${formatCompactIsoCurrency(movement.leftHighRisk.dealValue, companyCurrency)} left High risk`
      : null,
    movement.highRiskValueChanged.dealValue !== 0
      ? `High-risk Deal value ${movement.highRiskValueChanged.dealValue > 0 ? "increased" : "decreased"} by ${formatCompactIsoCurrency(
        Math.abs(movement.highRiskValueChanged.dealValue),
        companyCurrency,
      )}`
      : null,
  ].filter((value): value is string => value !== null);
  const riskStrength = [
    movement.riskIncreased.count > 0
      ? `${movement.riskIncreased.count} ${pluralise(movement.riskIncreased.count, "deal")} increased in risk`
      : null,
    movement.riskReduced.count > 0
      ? `${movement.riskReduced.count} ${pluralise(movement.riskReduced.count, "deal")} reduced in risk`
      : null,
  ].filter((value): value is string => value !== null);

  return {
    heading,
    label: "What changed",
    supporting: [...exposureMovement, ...riskStrength].join("; ")
      || `The ${movement.eligibleDealCount}-deal cohort had no material risk change.`,
  };
}

export function getPipelineRiskDriverCopy(
  insights: PipelineRiskL2Insights,
  companyCurrency: Currency,
): PipelineRiskSectionCopy {
  const theme = insights.drivers.dominantTheme;
  if (!theme) {
    return {
      heading: "No risk theme is available",
      label: "Why it is exposed",
    };
  }
  if (insights.drivers.state === "several_equal_themes") {
    return {
      heading: "Several risk themes have equal Deal value",
      label: "Why it is exposed",
      supporting: `${formatCompactIsoCurrency(theme.rawValue, companyCurrency)} appears in each leading theme.`,
    };
  }
  const indicator = insights.drivers.dominantIndicator;
  return {
    heading: `${theme.label} is the largest risk theme`,
    label: "Why it is exposed",
    supporting: indicator
      ? `${formatCompactIsoCurrency(theme.rawValue, companyCurrency)} across ${theme.count} ${pluralise(theme.count, "deal")}; ${indicator.label} is the largest signal.`
      : `${formatCompactIsoCurrency(theme.rawValue, companyCurrency)} across ${theme.count} ${pluralise(theme.count, "deal")}.`,
  };
}

export function getPipelineRiskConcentrationCopy(
  insights: PipelineRiskL2Insights,
  concentrations: Array<{ dimension: string; label: string; share: number }>,
): PipelineRiskSectionCopy {
  const timing = insights.timing.dominant;
  const concentration = concentrations.at(0);
  if (!timing && !concentration) {
    return {
      heading: "No material concentration is available",
      label: "Where risk sits",
    };
  }
  if (concentration && (!timing || concentration.share >= 0.5)) {
    return {
      heading: `${formatShare(concentration.share)} of exposed Deal value is concentrated in ${concentration.label}`,
      label: "Where risk sits",
      supporting: `${concentration.dimension} is the largest visible organisational concentration.`,
    };
  }
  return {
    heading: `${timing?.label ?? "Expected-close timing"} holds the largest risk exposure`,
    label: "Where risk sits",
    supporting: timing
      ? `${timing.count} exposed ${pluralise(timing.count, "deal")} are expected to close in this window.`
      : undefined,
  };
}

export function getPipelineRiskManagementCopy(
  insights: PipelineRiskL2Insights,
): PipelineRiskSectionCopy {
  const actions = insights.managementAttention.actions;
  if (!actions.length) {
    return {
      heading: "No immediate management priority identified",
      label: "Management attention",
      supporting: "No material issue requires review for this selection.",
    };
  }
  if (actions.length === 1) {
    return {
      heading: "One Pipeline Risk priority requires review",
      label: "Management attention",
    };
  }
  return {
    heading: `${actions.length} Pipeline Risk priorities require review`,
    label: "Management attention",
  };
}

export function getPipelineRiskManagementActionCopy(
  action: PipelineRiskManagementAction,
) {
  const name = action.dealName ?? action.concentration?.label ?? "Pipeline Risk exposure";
  if (action.type === "forecast_dependency") {
    return `Validate ${name}`;
  }
  if (action.type === "urgent_deal") {
    return `Review ${name}`;
  }
  if (action.type === "timing_dependency") {
    return "Review period-end exposure";
  }
  if (
    action.type === "worsening_outlook"
    && action.supportingMetrics.valueBasis === "deal_value"
  ) {
    return action.dealName
      ? `Review rising risk in ${action.dealName}`
      : "Review rising High-risk exposure";
  }
  if (action.type === "organisational_concentration") {
    return `Review ${name} concentration`;
  }
  return `Review ${name}`;
}

export function getPipelineRiskManagementActionSupportingCopy(
  action: PipelineRiskManagementAction,
  companyCurrency: Currency,
) {
  const facts: string[] = [];
  if (
    action.type === "organisational_concentration"
    && action.supportingMetrics.shareOfDealValue !== undefined
  ) {
    facts.push(`${formatShare(Number(action.supportingMetrics.shareOfDealValue))} of exposed Deal value`);
  } else if (
    action.type === "worsening_outlook"
    && action.supportingMetrics.valueBasis === "deal_value"
  ) {
    facts.push(`${formatCompactIsoCurrency(
      Number(action.supportingMetrics.dealValue ?? action.movementImpact ?? 0),
      companyCurrency,
    )} entered High risk`);
  } else if (action.shareOfForecast !== undefined) {
    facts.push(`${formatShare(action.shareOfForecast)} of Forecast`);
  } else if (action.forecastImpact !== undefined) {
    facts.push(formatCompactIsoCurrency(action.forecastImpact, companyCurrency));
  }
  if (action.urgency) facts.push(`${action.urgency} urgency`);
  if (action.currentRisk !== null && action.currentRisk !== undefined) {
    facts.push(`${Math.round(action.currentRisk)} pts`);
  }
  if (action.supportingMetrics.finalPeriodDays) {
    facts.push(`final ${action.supportingMetrics.finalPeriodDays} days`);
  }
  return facts.join(" · ");
}

export function getPipelineRiskDealEvidenceCopy(
  dealCount: number,
): PipelineRiskSectionCopy {
  return dealCount > 0
    ? {
      heading: dealCount === 1
        ? "One High-risk deal requires review"
        : `${dealCount} High-risk deals require review`,
      label: "High-risk deals",
      supporting: "Ranked by Deal value. Urgent marks deals in the 60-day Urgency shortlist.",
    }
    : {
      heading: "No High-risk deals to review",
      label: "High-risk deals",
    };
}
